/** Canonical wire names for code-execution tools (Python / R / Bash). */
const CODE_EXEC_TOOL_NAMES = new Set(['execute', 'bio_exec', 'python_exec', 'python'])

export function isCodeExecTool(toolName: string): boolean {
  return CODE_EXEC_TOOL_NAMES.has(toolName.toLowerCase())
}

/** @deprecated Prefer {@link isCodeExecTool}; kept for existing call sites and tests. */
export function isPythonToolInputTool(toolName: string): boolean {
  return isCodeExecTool(toolName)
}

/** True when the tool-call args block should be shown in the UI. */
export function hasToolInputForDisplay(argsText: string | undefined): boolean {
  const t = argsText?.trim()
  if (!t) return false
  if (t === '{}') return false
  try {
    const parsed: unknown = JSON.parse(t)
    if (
      parsed !== null &&
      typeof parsed === 'object' &&
      !Array.isArray(parsed) &&
      Object.keys(parsed as object).length === 0
    ) {
      return false
    }
  } catch {
    /* non-JSON args still shown */
  }
  return true
}

/**
 * Args JSON for display: hide no-arg tools, strip internal CWL ids where the UI shows a friendly summary.
 * Pretty-prints so the Parameters fence is readable instead of a single minified line.
 */
export function formatToolArgsTextForDisplay(toolName: string, argsText: string | undefined): string | undefined {
  if (!hasToolInputForDisplay(argsText)) return undefined
  const t = argsText!.trim()
  try {
    const parsed = JSON.parse(t) as Record<string, unknown>
    const key = toolName.trim().toLowerCase()
    if (key === 'get_cwl_workflow_params' && Object.keys(parsed).length === 1 && 'tool_name' in parsed) {
      return undefined
    }
    if (key === 'run_cwl_workflow') {
      const {tool_name: _tn, workflow_params, ...rest} = parsed
      const out: Record<string, unknown> = {...rest}
      if (workflow_params !== undefined) {
        if (typeof workflow_params === 'string') {
          try {
            out.workflow_inputs = JSON.parse(workflow_params)
          } catch {
            out.workflow_inputs = workflow_params
          }
        } else {
          out.workflow_inputs = workflow_params
        }
      }
      if (Object.keys(out).length === 0) return undefined
      return JSON.stringify(out, null, 2)
    }
    return JSON.stringify(parsed, null, 2)
  } catch {
    return t
  }
}

/** Turn common JSON-style escape sequences into real characters for display. */
export function decodePythonCodeEscapesForDisplay(code: string): string {
  return code
    .replace(/\\r\\n/g, '\n')
    .replace(/\\n/g, '\n')
    .replace(/\\t/g, '\t')
    .replace(/\\r/g, '\r')
}

export type CodeExecToolInputParts = {
  code: string
  /** Remaining keys when `code` is split out; empty object omitted in UI. */
  other: Record<string, unknown>
}

/** @deprecated Use {@link CodeExecToolInputParts}. */
export type PythonToolInputParts = CodeExecToolInputParts

/** Markdown fence language for Shiki (from `language` arg or tool defaults). */
export function fenceLanguageForCodeExecTool(
  toolName: string,
  toolInput: Record<string, unknown> | undefined,
): string {
  const lang = typeof toolInput?.language === 'string' ? toolInput.language.toLowerCase() : ''
  if (lang === 'r') return 'r'
  if (lang === 'bash' || lang === 'sh' || lang === 'shell') return 'bash'
  if (lang === 'python' || lang === 'py') return 'python'
  if (isCodeExecTool(toolName)) return 'python'
  return 'text'
}

/** Collapsed summary suffix from `language` (e.g. ` · r`). */
export function codeExecLanguageSummarySuffix(
  toolInput: Record<string, unknown> | undefined,
): string | undefined {
  const lang = typeof toolInput?.language === 'string' ? toolInput.language.trim() : ''
  if (!lang) return undefined
  return ` · ${lang}`
}

/**
 * When the tool is a code-exec tool and `tool_input.code` is a string, split code from other args
 * so the UI can render multiline code without JSON escaping noise.
 */
export function splitCodeExecToolInput(
  toolName: string,
  toolInput: Record<string, unknown> | undefined,
): CodeExecToolInputParts | null {
  if (!toolInput || typeof toolInput !== 'object') return null
  if (!isCodeExecTool(toolName)) return null
  const raw = toolInput.code
  if (typeof raw !== 'string') return null
  const code = decodePythonCodeEscapesForDisplay(raw)
  const other: Record<string, unknown> = {...toolInput}
  delete other.code
  return {code, other}
}

/** @deprecated Use {@link splitCodeExecToolInput}. */
export const splitPythonToolInput = splitCodeExecToolInput

/** Plain text for tool Result sections (no markdown fences — avoids Shiki / code-block chrome). */
export function formatToolResultPlainText(result: unknown): string {
  if (result == null || result === '') return ''
  if (typeof result === 'string') return result.trim()
  return JSON.stringify(result, null, 2)
}

/** @deprecated Use formatToolResultPlainText; kept for call sites that expect this name. */
export function formatPythonToolResultMarkdown(result: unknown): string {
  return formatToolResultPlainText(result)
}

function otherWithoutLanguage(other: Record<string, unknown>): Record<string, unknown> {
  if (!('language' in other)) return other
  const out = {...other}
  delete out.language
  return out
}

/** Code + remaining args + fence language for code-exec tool UIs. */
export function extractCodeExecForDisplay(
  toolName: string,
  toolInput: Record<string, unknown> | undefined,
): {code: string; other: Record<string, unknown>; fenceLanguage: string; summarySuffix?: string} {
  const split = splitCodeExecToolInput(toolName, toolInput)
  const fenceLanguage = fenceLanguageForCodeExecTool(toolName, toolInput)
  const summarySuffix = codeExecLanguageSummarySuffix(toolInput)
  if (split) {
    return {
      code: split.code,
      other: otherWithoutLanguage(split.other),
      fenceLanguage,
      summarySuffix,
    }
  }
  if (toolInput && typeof toolInput.code === 'string') {
    const code = decodePythonCodeEscapesForDisplay(toolInput.code)
    const other: Record<string, unknown> = {...toolInput}
    delete other.code
    return {code, other: otherWithoutLanguage(other), fenceLanguage, summarySuffix}
  }
  return {code: '', other: {}, fenceLanguage, summarySuffix}
}

/** Code string for display, with fallback when split returns null. */
export function extractPythonCodeForDisplay(
  toolName: string,
  toolInput: Record<string, unknown> | undefined,
): {code: string; other: Record<string, unknown>} {
  const {code, other} = extractCodeExecForDisplay(toolName, toolInput)
  return {code, other}
}
